import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import {
  type LeaderboardEntry,
  type OneDayRecord,
  type RankedEntry,
  formatRecordDay,
  topConquerors,
} from "./lib";
import type { DominationState } from "./use-domination-data";

// The four numeric columns, shared by the header and every row so the numbers
// sit on one vertical axis and can be compared down the card. Each track is
// sized for its widest content (a five-digit "19,077" total, a four-digit
// 30-day count) so no cell clips.
const STAT_COLS = "grid grid-cols-[4.25rem_2.75rem_2.75rem_3.5rem] gap-x-1.5";

const Stat = ({
  value,
  hint,
  strong,
}: {
  value: number | null;
  hint: string;
  strong?: boolean;
}) => (
  <span
    className={
      strong
        ? "text-right text-sm font-semibold tabular-nums"
        : "text-right text-[11px] tabular-nums text-muted-foreground"
    }
    title={hint}
  >
    {/* A dash, not a zero, for a number we don't have: the folded "Former
        curators" row is several people, so its pace has no value at all. */}
    {value == null ? "—" : value.toLocaleString()}
  </span>
);

/**
 * The three pace/volume numbers as one labelled line, for the narrow layout
 * where four columns would leave the name no room to render. "today" is
 * dropped at zero — a board where every idle curator reads "0 today" is noise.
 */
function paceLine(entry: LeaderboardEntry): string {
  return [
    // "avg" up front: a bare "354/day" reads as a lifetime rate, and this number is
    // the mean of the days the curator actually worked — days of 10 resolves or more.
    entry.avgDay != null ? `avg ${entry.avgDay}/day` : null,
    entry.last30d != null ? `${entry.last30d} last 30d` : null,
    entry.todayCount > 0 ? `${entry.todayCount} today` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

const Row = ({
  ranked,
  youUid,
  color,
  sharePct,
}: {
  ranked: RankedEntry;
  youUid: string | null;
  color: string;
  sharePct: number;
}) => {
  const { entry, rank } = ranked;
  const isYou = youUid != null && entry.uid === youUid;
  const pace = paceLine(entry);
  return (
    <div className="relative border-b last:border-b-0">
      {/* The row's slice of EVERY resolve on the board, drawn behind the content
          in the curator's own map colour — so the widest bar is the biggest
          slice of the work done, and the bars never fill the card. It is
          positioned first in the DOM with no `z-index`, so it paints under the
          row that follows it while staying behind every number. */}
      <span
        aria-hidden
        className="absolute inset-y-1 left-0 rounded-sm"
        style={{ width: `${sharePct}%`, background: color, opacity: 0.12 }}
      />
      <div className="relative flex items-center gap-2 py-1.5">
        <span className="w-4 text-right text-[10px] tabular-nums text-muted-foreground">
          {rank}
        </span>
        <Avatar
          className="h-7 w-7"
          style={{
            // a ring in the curator's map colour; offset for "you"
            boxShadow: isYou
              ? `0 0 0 2px var(--card), 0 0 0 4px ${color}`
              : `0 0 0 2px ${color}`,
          }}
        >
          <AvatarImage src={entry.photoUrl ?? undefined} />
          <AvatarFallback className="text-[11px]">
            {entry.displayName.charAt(0).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="min-w-0 flex-1 truncate text-sm font-medium">
              {entry.displayName}
              {isYou && (
                <span className="ml-1 text-[10px]" style={{ color }}>
                  · you
                </span>
              )}
            </span>
            {/* Below `sm` the four columns have nowhere to go, so the total
                rides the name line and the rest wrap underneath it. */}
            <span className="text-sm font-semibold tabular-nums sm:hidden">
              {entry.total.toLocaleString()}
            </span>
          </div>
          {pace && (
            <div className="text-[11px] text-muted-foreground sm:hidden">
              {pace}
            </div>
          )}
        </div>
        <div className={`${STAT_COLS} hidden items-center sm:grid`}>
          <Stat
            value={entry.avgDay}
            hint="Average resolves on a day with 10 or more resolves"
          />
          <Stat value={entry.last30d} hint="Resolves in the last 30 days" />
          <Stat value={entry.todayCount} hint="Resolves today" />
          <Stat value={entry.total} hint="All-time resolves" strong />
        </div>
      </div>
    </div>
  );
};

/**
 * The one-day record, drawn above the ranked list rather than inside it: the holder need
 * not appear in the top rows at all, since a curator can own the biggest single day on the
 * board and still rank low on all-time volume. The crown marks the record, and the line
 * under the name says when it was set — a record with no date is just a big number.
 */
const RecordRow = ({
  record,
  youUid,
  color,
}: {
  record: OneDayRecord;
  youUid: string | null;
  color: string;
}) => {
  const { entry, count, day } = record;
  const isYou = youUid != null && entry.uid === youUid;
  return (
    <div className="mb-2 flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 px-2 py-1.5">
      {/* The avatar is the positioning context so the crown can ride its top-right
          corner at any avatar size. */}
      <span className="relative shrink-0">
        <Avatar
          className="h-9 w-9"
          style={{
            boxShadow: isYou
              ? `0 0 0 2px var(--card), 0 0 0 4px ${color}`
              : `0 0 0 2px ${color}`,
          }}
        >
          <AvatarImage src={entry.photoUrl ?? undefined} />
          <AvatarFallback className="text-[11px]">
            {entry.displayName.charAt(0).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        {/* Decorative: the "Record set on" line below already carries the meaning, so a
            screen reader gains nothing from the crown and would read it as noise. */}
        <span
          aria-hidden
          className="pointer-events-none absolute -top-1.5 -right-1.5 text-[13px] leading-none"
        >
          👑
        </span>
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">
          {entry.displayName}
          {isYou && (
            <span className="ml-1 text-[10px] text-muted-foreground">
              · you
            </span>
          )}
        </div>
        <div className="truncate text-[11px] text-muted-foreground">
          Record set on {formatRecordDay(day)}
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div className="text-sm font-semibold tabular-nums">
          {count.toLocaleString()}
        </div>
        <div className="text-[10px] text-muted-foreground">in one day</div>
      </div>
    </div>
  );
};

export const TopConquerorsCard = ({
  state,
  className,
}: {
  state: DominationState;
  className?: string;
}) => {
  const { data, youUid, isLoading, isError } = state;
  const board = data ? topConquerors(data.leaderboard, youUid) : null;
  const record = data?.record ?? null;
  const empty = !isLoading && (!board || board.top.length === 0);
  // Each bar is the row's slice of every resolve on the board — the same figure
  // the map card prints as "N total resolves", so the two cards agree.
  const grandTotal = data?.grandTotal ?? 0;
  const shareOf = (total: number) =>
    grandTotal > 0 ? (100 * total) / grandTotal : 0;

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          🏆 Top conquerors
        </CardTitle>
        <CardDescription>
          All-time resolves. Pace is the average of a day with 10 or more
          resolves.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3 py-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : isError ? (
          <div className="py-8 text-center text-xs text-muted-foreground">
            Couldn't load domination data.
          </div>
        ) : empty ? (
          <div className="py-8 text-center text-xs text-muted-foreground">
            No territory claimed yet. Resolve your first HITL to plant a flag.
          </div>
        ) : (
          <div>
            {record && (
              <RecordRow
                record={record}
                youUid={youUid}
                color={
                  data!.colorByUid.get(record.entry.uid) ?? "var(--chart-2)"
                }
              />
            )}
            <div className="hidden items-center gap-2 pb-1 text-[10px] uppercase tracking-wide text-muted-foreground sm:flex">
              <span className="w-4" />
              <span className="w-7" />
              <span className="flex-1" />
              <div className={`${STAT_COLS} text-right`}>
                <span className="tracking-normal">avg/day</span>
                <span>30d</span>
                <span>today</span>
                <span>total</span>
              </div>
            </div>
            {board!.top.map((r) => (
              <Row
                key={r.entry.uid}
                ranked={r}
                youUid={youUid}
                color={data!.colorByUid.get(r.entry.uid) ?? "var(--chart-2)"}
                sharePct={shareOf(r.entry.total)}
              />
            ))}
            {board!.pinned && (
              <div className="mt-1 border-t border-dashed pt-1">
                <Row
                  ranked={board!.pinned}
                  youUid={youUid}
                  color={
                    data!.colorByUid.get(board!.pinned.entry.uid) ??
                    "var(--chart-2)"
                  }
                  sharePct={shareOf(board!.pinned.entry.total)}
                />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
